<template>
  <div class="property-table">
    <!-- Table Controls -->
    <div class="flex justify-between items-center mb-4">
      <div class="flex space-x-2">
        <!-- Payment Status Filter -->
        <select 
          v-model="filters.paymentStatus" 
          class="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">All Payment Statuses</option>
          <option value="paid">Paid</option>
          <option value="unpaid">Unpaid</option>
        </select>
        
        <!-- Price Range -->
        <input 
          v-model.number="filters.minPrice" 
          type="number" 
          placeholder="Min Price" 
          class="border rounded-lg px-3 py-2 w-32 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
        <input 
          v-model.number="filters.maxPrice" 
          type="number" 
          placeholder="Max Price" 
          class="border rounded-lg px-3 py-2 w-32 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
        
        <!-- Clear Filters -->
        <button 
          @click="clearFilters" 
          class="px-3 py-2 border rounded-lg hover:bg-gray-50"
        >
          Clear Filters
        </button>
      </div>
      
      <!-- Add Property Button (if user has appropriate role) -->
      <button 
        v-if="authStore.userRole === 'admin' || authStore.userRole === 'agent'"
        @click="$emit('add-property')" 
        class="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
      >
        <i class="fas fa-plus mr-2"></i> Add Property
      </button>
    </div>
    
    <!-- Table -->
    <div class="overflow-x-auto bg-white rounded-lg shadow">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th 
              v-for="column in columns" 
              :key="column.field"
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
              @click="sortBy(column.field)"
            >
              {{ column.label }}
              <i 
                v-if="sortField === column.field" 
                :class="sortDirection === 'asc' ? 'fas fa-sort-up ml-1' : 'fas fa-sort-down ml-1'"
              ></i>
            </th>
            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-if="loading" class="text-center">
            <td :colspan="columns.length + 1" class="px-6 py-4">
              <div class="flex justify-center">
                <i class="fas fa-spinner fa-spin text-primary-600 text-2xl"></i>
              </div>
            </td>
          </tr>
          <tr v-else-if="filteredProperties.length === 0" class="text-center">
            <td :colspan="columns.length + 1" class="px-6 py-4 text-gray-500">
              No properties found with the current filters
            </td>
          </tr>
          <tr 
            v-for="property in sortedProperties" 
            :key="property.id"
            class="hover:bg-gray-50"
          >
            <td class="px-6 py-4 whitespace-nowrap">
              {{ property.title }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              {{ property.address }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              ${{ property.price.toLocaleString() }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              {{ property.size }} sqft
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              {{ property.owner_name }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span 
                :class="{
                  'bg-green-100 text-green-800': property.payment_status === 'paid',
                  'bg-red-100 text-red-800': property.payment_status === 'unpaid'
                }"
                class="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full"
              >
                {{ property.payment_status }}
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
              <button 
                @click="$emit('view-property', property.id)" 
                class="text-primary-600 hover:text-primary-900 mr-3"
              >
                <i class="fas fa-eye"></i>
              </button>
              <button 
                v-if="authStore.userRole === 'admin' || authStore.userRole === 'agent'"
                @click="$emit('edit-property', property.id)" 
                class="text-blue-600 hover:text-blue-900 mr-3"
              >
                <i class="fas fa-edit"></i>
              </button>
              <button 
                v-if="authStore.userRole === 'admin'"
                @click="$emit('delete-property', property.id)" 
                class="text-red-600 hover:text-red-900"
              >
                <i class="fas fa-trash"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue';
import { usePropertiesStore } from '../stores/properties';
import { useAuthStore } from '../stores/auth';

const props = defineProps({
  loading: {
    type: Boolean,
    default: false
  }
});

defineEmits(['view-property', 'edit-property', 'delete-property', 'add-property']);

const propertiesStore = usePropertiesStore();
const authStore = useAuthStore();

// Table columns
const columns = [
  { field: 'title', label: 'Property' },
  { field: 'address', label: 'Address' },
  { field: 'price', label: 'Price' },
  { field: 'size', label: 'Size' },
  { field: 'owner_name', label: 'Owner' },
  { field: 'payment_status', label: 'Payment Status' }
];

// Sorting
const sortField = ref('title');
const sortDirection = ref('asc');

// Filters
const filters = ref({
  paymentStatus: '',
  minPrice: '',
  maxPrice: ''
});

// Reset filters
const clearFilters = () => {
  filters.value = {
    paymentStatus: '',
    minPrice: '',
    maxPrice: ''
  };
};

// Apply filters to properties store
watch(filters, (newFilters) => {
  propertiesStore.setFilters({
    paymentStatus: newFilters.paymentStatus || null,
    minPrice: newFilters.minPrice || null,
    maxPrice: newFilters.maxPrice || null
  });
}, { deep: true });

// Computed properties
const filteredProperties = computed(() => {
  return propertiesStore.filteredProperties;
});

const sortedProperties = computed(() => {
  return [...filteredProperties.value].sort((a, b) => {
    let compareA = a[sortField.value];
    let compareB = b[sortField.value];
    
    // Special handling for string comparisons
    if (typeof compareA === 'string') {
      compareA = compareA.toLowerCase();
      compareB = compareB.toLowerCase();
    }
    
    if (compareA < compareB) {
      return sortDirection.value === 'asc' ? -1 : 1;
    }
    if (compareA > compareB) {
      return sortDirection.value === 'asc' ? 1 : -1;
    }
    return 0;
  });
});

// Sort function
const sortBy = (field) => {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortField.value = field;
    sortDirection.value = 'asc';
  }
};
</script>
